import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAudioManager } from '@/lib/audio-manager';
import { Volume2, VolumeX, Play } from 'lucide-react';

export function AudioSettings() {
  const { settings, updateSettings, playSound, isReady } = useAudioManager();
  const [testPlaying, setTestPlaying] = useState<string | null>(null);

  const testSound = async (soundType: any, label: string) => {
    setTestPlaying(label);
    await playSound(soundType);
    setTimeout(() => setTestPlaying(null), 500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5" />
          Audio Settings
        </CardTitle>
        <CardDescription>
          Customize audio feedback for moves, captures, and game events
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Master Controls */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="audio-muted" className="text-sm font-medium">
              {settings.muted ? 'Audio Muted' : 'Audio Enabled'}
            </Label>
            <Switch
              id="audio-muted"
              checked={!settings.muted}
              onCheckedChange={(checked) => updateSettings({ muted: !checked })}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Master Volume</Label>
            <div className="flex items-center gap-3">
              {settings.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              <Slider
                value={[settings.masterVolume * 100]}
                onValueChange={([value]) => updateSettings({ masterVolume: value / 100 })}
                max={100}
                step={5}
                className="flex-1"
                disabled={settings.muted}
              />
              <span className="text-sm text-muted-foreground w-12">
                {Math.round(settings.masterVolume * 100)}%
              </span>
            </div>
          </div>
        </div>

        {/* Volume Categories */}
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Move Sounds</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => testSound('move', 'move')}
                disabled={settings.muted || testPlaying === 'move'}
              >
                <Play className="h-3 w-3" />
              </Button>
            </div>
            <Slider
              value={[settings.moveVolume * 100]}
              onValueChange={([value]) => updateSettings({ moveVolume: value / 100 })}
              max={100}
              step={5}
              disabled={settings.muted}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Capture Sounds</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => testSound('queen-capture', 'capture')}
                disabled={settings.muted || testPlaying === 'capture'}
              >
                <Play className="h-3 w-3" />
              </Button>
            </div>
            <Slider
              value={[settings.captureVolume * 100]}
              onValueChange={([value]) => updateSettings({ captureVolume: value / 100 })}
              max={100}
              step={5}
              disabled={settings.muted}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Move Quality Feedback</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => testSound('brilliant', 'feedback')}
                disabled={settings.muted || testPlaying === 'feedback'}
              >
                <Play className="h-3 w-3" />
              </Button>
            </div>
            <Slider
              value={[settings.feedbackVolume * 100]}
              onValueChange={([value]) => updateSettings({ feedbackVolume: value / 100 })}
              max={100}
              step={5}
              disabled={settings.muted}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Victory Celebrations</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => testSound('victory-hard', 'celebration')}
                disabled={settings.muted || testPlaying === 'celebration'}
              >
                <Play className="h-3 w-3" />
              </Button>
            </div>
            <Slider
              value={[settings.celebrationVolume * 100]}
              onValueChange={([value]) => updateSettings({ celebrationVolume: value / 100 })}
              max={100}
              step={5}
              disabled={settings.muted}
            />
          </div>
        </div>

        {/* Sound Test Section */}
        <div className="space-y-3 pt-4 border-t">
          <Label className="text-sm font-medium">Test Sounds</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => testSound('great', 'great-move')}
              disabled={settings.muted || testPlaying === 'great-move'}
            >
              Great Move
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => testSound('blunder', 'blunder')}
              disabled={settings.muted || testPlaying === 'blunder'}
            >
              Blunder
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => testSound('check', 'check')}
              disabled={settings.muted || testPlaying === 'check'}
            >
              Check
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => testSound('checkmate', 'checkmate')}
              disabled={settings.muted || testPlaying === 'checkmate'}
            >
              Checkmate
            </Button>
          </div>
        </div>

        {!isReady && (
          <p className="text-sm text-muted-foreground">
            Audio system is initializing...
          </p>
        )}
      </CardContent>
    </Card>
  );
}